import React from 'react';
import DynamicFormWidget, { IDynamicFormLayout } from 'src/components/dynamic-widget/DynamicFormWidget';
import { DynamicField, DynamicFieldTypes } from 'src/components/dynamic-widget';
import { DataService } from 'src/services/data-service';
import GenericDialog from 'src/components/GenericDialog';
import { ROLE } from 'src/services/permission-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface Employee {
  Id: string;
  Name: string;
  Email: string;
  PhoneNumber: string;
  Role?: string;
}

interface EmployeeDrawerProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  isAdmin: boolean;
  updateData?: Employee;
}

const EmployeeDrawer: React.FC<EmployeeDrawerProps> = ({ 
  open, 
  onClose, 
  onSuccess, 
  isAdmin, 
  updateData 
}) => {
  const isEdit = !!updateData;
  const dispatch = useAppDispatch();

  const formConfig: Record<string, DynamicFieldTypes> = {
    name: {
      type: DynamicField.TEXT,
      required: true,
      name: 'name',
      title: 'Full Name',
      value: updateData?.Name || '',
      disabled: false,
      placeholder: 'Enter full name',
      minChars: 2,
      maxChars: 100
    },
    email: {
      type: DynamicField.EMAIL,
      required: true,
      name: 'email',
      title: 'Email Address',
      value: updateData?.Email || '',
      disabled: false,
      placeholder: 'email@example.com'
    },
    phoneNumber: {
      type: DynamicField.PHONENUMBER,
      required: true,
      name: 'phoneNumber',
      title: 'Phone Number',
      value: updateData?.PhoneNumber || '',
      disabled: false,
      placeholder: '+961 23 432 345'
    },
    role: {
      type: DynamicField.SELECT,
      required: true,
      name: 'role',
      title: 'Role',
      value: updateData?.Role || ROLE.EMPLOYEE,
      items: {
        [ROLE.ADMIN]: 'Admin',
        [ROLE.EMPLOYEE]: 'Employee',
      },
      disabled: false
    },
    password: {
      type: DynamicField.TEXT,
      required: !isEdit,
      name: 'password',
      title: 'Password',
      value: '',
      disabled: false,
      inputType: 'password',
      hidden: isEdit,
      minChars: 8,
      placeholder: 'Minimum 8 characters',
      customValidation: (value: any) => {
        if (!isEdit && value) {
          // Check for at least one uppercase, one lowercase, one number, and one special character
          const hasUpperCase = /[A-Z]/.test(value);
          const hasLowerCase = /[a-z]/.test(value);
          const hasNumber = /[0-9]/.test(value);
          const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);
          
          if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar) {
            return 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character';
          }
        }
        return '';
      }
    },
    confirmPassword: {
      type: DynamicField.TEXT,
      required: !isEdit,
      name: 'confirmPassword',
      title: 'Confirm Password',
      value: '',
      disabled: false,
      inputType: 'password',
      hidden: isEdit,
      placeholder: 'Re-enter password',
      customValidation: (value: any, allValues: Record<string, any>) => {
        if (!isEdit && value !== allValues.password) {
          return 'Passwords do not match';
        }
        return '';
      }
    }
  };

  // Define the layout with groups
  const formLayout: IDynamicFormLayout = {
    groups: [
      {
        title: 'Personal Information',
        fields: ['name', 'phoneNumber', 'email'],
        columns: 2
      },
      {
        title: 'Access Control',
        fields: ['role'],
        columns: 1
      },
      {
        title: 'Security',
        fields: ['password', 'confirmPassword'],
        columns: isEdit ? 1 : 2
      }
    ]
  };

  const submit = async (data: Record<string, any>) => {
    try {
      // Prepare the payload according to API requirements
      const payload = {
        name: data.name,
        email: data.email,
        phoneNumber: data.phoneNumber,
        role: data.role || ROLE.EMPLOYEE
      };

      // Only include password for new employees
      if (!isEdit) {
        (payload as any).password = data.password;
      }

      if (isEdit && updateData) {
        (payload as any).Id = updateData.Id;
        const req = await DataService.post(`api/Employee/update`, payload);
        const resJson = await req.json();
        
        if (req.ok && resJson.success) {
          dispatch(showAlert({ message: 'Employee updated successfully!', type: 'success' }));
          onSuccess();
          onClose();
          return true;
        } else {
          dispatch(showAlert({ message: resJson.message || 'Failed to update user', type: 'error' }));
          // Optionally close the drawer on error, but usually keep open for correction
          return false;
        }
      } else {
        const req = await DataService.post('api/Employee/create', payload);
        const resJson = await req.json();
        
        if (req.ok && resJson.success) {
          dispatch(showAlert({ message: 'Employee created successfully!', type: 'success' }));
          onSuccess();
          onClose();
          return true;
        } else {
          dispatch(showAlert({ message: resJson.Message || 'Failed to create user', type: 'error' }));
          return false;
        }
      }
    } catch (error) {
      console.error('Submission error:', error);
      dispatch(showAlert({ message: 'Network error. Please check your connection and try again.', type: 'error' }));
      return false;
    }
  };

  if (!isAdmin) return null;

  return (
    <GenericDialog 
      title={isEdit ? 'Edit User' : 'Add New User'} 
      open={open} 
      onClose={onClose}
      // maxWidth="sm"
      // fullWidth
    >
      <DynamicFormWidget
        title=""
        drawerMode
        fields={formConfig}
        onSubmit={submit}
        submitButtonText={isEdit ? 'Update User' : 'Create User'}
        showCancelButton
        onCancel={onClose}
        initialValues={
          updateData 
            ? {
                name: updateData.Name,
                email: updateData.Email,
                phoneNumber: updateData.PhoneNumber,
                role: updateData.Role
              }
            : {}
        }
        enableEnterSubmit
        autoFocusFirst
        layout={formLayout}
        validationMode="onChange"
      />
    </GenericDialog>
  );
};

export default EmployeeDrawer;